export const SHARE_LINK_COMMENT_HIGHLIGHT_CLASS = "share-link-comment-text-highlight"
export const SHARE_LINK_COMMENT_HIGHLIGHT_EMPHASIS_SELECTED = "selected"
export const SHARE_LINK_COMMENT_HIGHLIGHT_EMPHASIS_MUTED = "muted"
// A comment still being composed: the author's original text selection, kept visible while they type.
export const SHARE_LINK_COMMENT_HIGHLIGHT_EMPHASIS_DRAFT = "draft"
const TEXT_BLOCK_TAGS = new Set([
    "ADDRESS",
    "ARTICLE",
    "ASIDE",
    "BLOCKQUOTE",
    "DD",
    "DIV",
    "DL",
    "DT",
    "FIGCAPTION",
    "FIGURE",
    "FOOTER",
    "H1",
    "H2",
    "H3",
    "H4",
    "H5",
    "H6",
    "HEADER",
    "LI",
    "MAIN",
    "NAV",
    "OL",
    "P",
    "PRE",
    "SECTION",
    "TD",
    "TH",
    "TR",
    "UL",
])

/**
 * Walks the DOM under `root` and wraps the first matching occurrence of
 * `targetText` in a highlight span. When `commentId` is provided it is stored on
 * the injected span as `dataset.commentId` so click delegation can resolve the
 * owning comment.
 *
 * @param {Element} root
 * @param {string} targetText
 * @param {string} [commentId]
 * @param {"selected"|"muted"|"draft"} [emphasis]
 * @param {{ beforeText?: string, afterText?: string }} [context]
 * @returns {boolean} whether a match was wrapped
 */
export function highlightText(root, targetText, commentId, emphasis, context) {
    const searchText = normalizeSearchText(targetText)
    if (searchText.length < 2) return false
    return highlightTextAcrossNodes(getHighlightTextNodes(root), searchText, commentId, emphasis, context)
}

export function getTextContextForRange(root, range, contextLength) {
    const index = buildSearchIndex(getHighlightTextNodes(root))
    const start = findRangeStartIndex(index.positions, range.startContainer, range.startOffset)
    const end = findRangeEndIndex(index.positions, range.endContainer, range.endOffset)
    if (start === -1 || end === -1 || end < start) return null
    return {
        start,
        end,
        beforeText: index.text.slice(0, start).trimEnd().slice(-contextLength),
        afterText: index.text.slice(end).trimStart().slice(0, contextLength),
    }
}

/**
 * Wraps the first case-insensitive occurrence of `searchText` within a single
 * text node in a highlight span, tagging it with `commentId` when present.
 *
 * @param {Text} textNode
 * @param {string} searchText
 * @param {string} [commentId]
 * @param {"selected"|"muted"|"draft"} [emphasis]
 * @returns {boolean} whether a match was wrapped
 */
export function highlightTextNode(textNode, searchText, commentId, emphasis) {
    const sourceText = textNode.textContent ?? ""
    const index = sourceText.toLowerCase().indexOf(searchText.toLowerCase())
    if (index === -1) return false

    const range = document.createRange()
    range.setStart(textNode, index)
    range.setEnd(textNode, index + searchText.length)
    range.surroundContents(createHighlightSpan(commentId, emphasis))
    return true
}

/**
 * Removes every injected highlight span under `root`, restoring the original
 * text content.
 *
 * @param {Element} root
 */
export function clearTextHighlights(root) {
    const highlights = [...root.querySelectorAll(`.${SHARE_LINK_COMMENT_HIGHLIGHT_CLASS}`)]
    for (const highlight of highlights) {
        highlight.replaceWith(document.createTextNode(highlight.textContent ?? ""))
    }
    root.normalize()
}

/**
 * Splits a comment's anchor target text into the discrete fragments we will try
 * to highlight. Short texts highlight whole; long texts split on sentence
 * boundaries so we highlight a meaningful chunk rather than a paragraph.
 *
 * @param {string} value
 * @returns {string[]}
 */
export function getHighlightFragments(value) {
    const text = normalizeSearchText(value)
    if (!text) return []
    if (text.length <= 160) return [text]
    return text.split(/(?<=[.!?])\s+/).filter(isUsefulHighlightFragment)
}

/**
 * Given a click target, walk up to the nearest highlight span and return the
 * comment id stored on it (if any). Pure so it can be unit-tested in isolation.
 *
 * @param {EventTarget|null} target
 * @returns {string|null}
 */
export function resolveClickedCommentId(target) {
    const highlight = target?.closest?.(`.${SHARE_LINK_COMMENT_HIGHLIGHT_CLASS}`)
    if (!highlight) return null
    return highlight.dataset?.commentId ?? null
}

/**
 * Maps the party of the comment driving a highlight to the MUI theme palette key
 * used as the blend base. Share-link (recipient) comments read as "warning";
 * everything else (internal) reads as "primary". Pure so it is unit-testable.
 *
 * @param {{ authorType?: string }|null} comment
 * @returns {"warning"|"primary"}
 */
export function getHighlightPartyColorKey(comment) {
    if (comment?.authorType === "share-link") return "warning"
    return "primary"
}

function isUsefulHighlightFragment(value) {
    return value.trim().length >= 20
}

function getHighlightTextNodes(root) {
    const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT, {
        acceptNode(node) {
            if (!node.textContent?.trim()) return NodeFilter.FILTER_REJECT
            if (node.parentElement?.closest(`.${SHARE_LINK_COMMENT_HIGHLIGHT_CLASS}`)) return NodeFilter.FILTER_REJECT
            return NodeFilter.FILTER_ACCEPT
        },
    })
    const nodes = []
    let node
    while ((node = walker.nextNode())) {
        nodes.push(node)
    }
    return nodes
}

function highlightTextAcrossNodes(nodes, searchText, commentId, emphasis, context) {
    const index = buildSearchIndex(nodes)
    const matchStart = findContextualMatchStart(index.text, searchText, context)
    if (matchStart === -1) return false
    const segments = getMatchSegments(index.positions, matchStart, searchText.length)
    if (!segments.length) return false
    wrapSegments(segments, commentId, emphasis)
    return true
}

function findContextualMatchStart(indexText, searchText, context) {
    const lowerIndexText = indexText.toLowerCase()
    const lowerSearchText = searchText.toLowerCase()
    const matches = findMatchStarts(lowerIndexText, lowerSearchText)
    if (!matches.length) return -1
    const beforeText = normalizeSearchText(context?.beforeText).toLowerCase()
    const afterText = normalizeSearchText(context?.afterText).toLowerCase()
    if (!beforeText && !afterText) return matches[0]
    return getBestContextualMatch({ matches, lowerIndexText, lowerSearchText, beforeText, afterText })
}

function findMatchStarts(indexText, searchText) {
    const matches = []
    let start = indexText.indexOf(searchText)
    while (start !== -1) {
        matches.push(start)
        start = indexText.indexOf(searchText, start + 1)
    }
    return matches
}

function getBestContextualMatch({ matches, lowerIndexText, lowerSearchText, beforeText, afterText }) {
    let bestStart = matches[0]
    let bestScore = 0
    for (const start of matches) {
        const score = getContextMatchScore({ lowerIndexText, lowerSearchText, start, beforeText, afterText })
        if (score > bestScore) {
            bestScore = score
            bestStart = start
        }
    }
    return bestStart
}

function getContextMatchScore({ lowerIndexText, lowerSearchText, start, beforeText, afterText }) {
    let score = 0
    if (beforeText && lowerIndexText.slice(0, start).trimEnd().endsWith(beforeText)) score += beforeText.length
    if (
        afterText &&
        lowerIndexText
            .slice(start + lowerSearchText.length)
            .trimStart()
            .startsWith(afterText)
    ) {
        score += afterText.length
    }
    return score
}

function buildSearchIndex(nodes) {
    const text = []
    const positions = []
    let lastWasSpace = true
    for (let nodeIndex = 0; nodeIndex < nodes.length; nodeIndex += 1) {
        const node = nodes[nodeIndex]
        lastWasSpace = appendNodeToSearchIndex({ node, text, positions, lastWasSpace })
        if (!lastWasSpace && shouldSeparateTextNodes(node, nodes[nodeIndex + 1])) {
            text.push(" ")
            positions.push(null)
            lastWasSpace = true
        }
    }
    while (text[text.length - 1] === " ") {
        text.pop()
        positions.pop()
    }
    return { text: text.join(""), positions }
}

function appendNodeToSearchIndex({ node, text, positions, lastWasSpace }) {
    const source = node.textContent ?? ""
    let index = 0
    let isLastSpace = lastWasSpace
    while (index < source.length) {
        if (isWhitespace(source[index])) {
            const start = index
            while (index < source.length && isWhitespace(source[index])) index += 1
            if (!isLastSpace) {
                text.push(" ")
                positions.push({ node, start, end: index })
                isLastSpace = true
            }
        } else {
            text.push(source[index])
            positions.push({ node, start: index, end: index + 1 })
            index += 1
            isLastSpace = false
        }
    }
    return isLastSpace
}

function getMatchSegments(positions, matchStart, matchLength) {
    const segments = []
    const matchEnd = matchStart + matchLength
    for (let index = matchStart; index < matchEnd; index += 1) {
        const position = positions[index]
        if (!position) continue
        const last = segments[segments.length - 1]
        if (last?.node === position.node && last.end === position.start) {
            last.end = position.end
        } else {
            segments.push({ ...position })
        }
    }
    return segments
}

function findRangeStartIndex(positions, node, offset) {
    for (let index = 0; index < positions.length; index += 1) {
        const position = positions[index]
        if (!isPositionForNode(position, node)) continue
        if (offset <= position.start || offset < position.end) return index
    }
    return -1
}

function findRangeEndIndex(positions, node, offset) {
    let lastIndex = -1
    for (let index = 0; index < positions.length; index += 1) {
        const position = positions[index]
        if (!isPositionForNode(position, node)) continue
        if (offset <= position.start) return index
        if (offset <= position.end) return index + 1
        lastIndex = index + 1
    }
    return lastIndex
}

function isPositionForNode(position, node) {
    return !!position && position.node === node
}

function wrapSegments(segments, commentId, emphasis) {
    for (let index = segments.length - 1; index >= 0; index -= 1) {
        const segment = segments[index]
        const range = document.createRange()
        range.setStart(segment.node, segment.start)
        range.setEnd(segment.node, segment.end)
        range.surroundContents(createHighlightSpan(commentId, emphasis))
    }
}

function createHighlightSpan(commentId, emphasis) {
    const span = document.createElement("span")
    span.className = SHARE_LINK_COMMENT_HIGHLIGHT_CLASS
    if (commentId) span.dataset.commentId = commentId
    if (emphasis) span.dataset.highlightEmphasis = emphasis
    return span
}

function isWhitespace(character) {
    return /\s/.test(character)
}

function shouldSeparateTextNodes(currentNode, nextNode) {
    if (!nextNode) return false
    const currentBlock = getTextBlockElement(currentNode)
    const nextBlock = getTextBlockElement(nextNode)
    return !!currentBlock && !!nextBlock && currentBlock !== nextBlock
}

function getTextBlockElement(node) {
    let element = node?.parentElement
    while (element) {
        if (TEXT_BLOCK_TAGS.has(element.tagName)) return element
        element = element.parentElement
    }
    return null
}

export function normalizeSearchText(value) {
    return decodeHtmlEntities(stripHtml(value)).replace(/\s+/g, " ").trim()
}

function stripHtml(value) {
    return `${value ?? ""}`.replace(/<[^>]+>/g, " ")
}

function decodeHtmlEntities(value) {
    return `${value}`
        .replace(/&nbsp;/g, " ")
        .replace(/&amp;/g, "&")
        .replace(/&lt;/g, "<")
        .replace(/&gt;/g, ">")
        .replace(/&quot;/g, '"')
        .replace(/&#39;/g, "'")
        .replace(/&#(\d+);/g, decodeNumericEntity)
}

function decodeNumericEntity(match, code) {
    return String.fromCodePoint(Number.parseInt(code, 10))
}
